import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Upload, FileText, X, Loader2, CheckCircle2, IndianRupee, ListChecks,
} from "lucide-react";

const PAPER = ["A4", "A5", "Letter", "Legal", "A3"];

export default function BulkUpload() {
  const { user, refreshMe } = useAuth();
  const { t } = useI18n();
  const [files, setFiles] = useState([]);
  const [drag, setDrag] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [form, setForm] = useState({
    copies: 1, color: false, duplex: false,
    paper_size: "A4", orientation: "portrait", printer_id: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/printers");
      setPrinters(data);
      const def = data.find((p) => p.is_default) || data[0];
      if (def) setForm((f) => ({ ...f, printer_id: def.id }));
    })();
  }, []);

  const addFiles = (list) => {
    const pdfs = Array.from(list).filter(
      (f) => f.name.toLowerCase().endsWith(".pdf") && f.size <= 15 * 1024 * 1024
    );
    if (pdfs.length === 0) return toast.error("Only PDFs up to 15MB each");
    setFiles((prev) => [...prev, ...pdfs].slice(0, 20));
  };
  const removeFile = (idx) => setFiles((f) => f.filter((_, i) => i !== idx));
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  }, []);

  const submit = async () => {
    if (files.length === 0) return toast.error("Add at least one file");
    setSubmitting(true);
    setResults(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("copies", String(form.copies));
      fd.append("color", String(form.color));
      fd.append("duplex", String(form.duplex));
      fd.append("paper_size", form.paper_size);
      fd.append("orientation", form.orientation);
      if (form.printer_id) fd.append("printer_id", form.printer_id);
      const { data } = await api.post("/orders/bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResults(data);
      const ok = data.results.filter((r) => r.order).length;
      toast.success(`${ok}/${data.count} print jobs queued`);
      await refreshMe();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const totalSize = files.reduce((s, f) => s + f.size, 0);

  return (
    <div className="space-y-6 max-w-5xl" data-testid="bulk-upload-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">{t("nav.bulkPrint")}</p>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">{t("bulk.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("bulk.subtitle")}</p>
      </div>

      <Card className="p-6 border-border bg-card">
        <label
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          className={`block cursor-pointer rounded-xl border-2 border-dashed p-10 text-center transition-all
            ${drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-secondary/40"}`}
          data-testid="bulk-dropzone"
        >
          <div className="h-14 w-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
            <Upload size={24} />
          </div>
          <p className="mt-4 font-display text-lg font-medium">{t("bulk.dropzone")}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("bulk.hint")}</p>
          <input
            type="file"
            multiple
            accept="application/pdf"
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
            data-testid="bulk-file-input"
          />
        </label>
      </Card>

      {files.length > 0 && (
        <div className="grid lg:grid-cols-5 gap-6">
          <Card className="lg:col-span-3 p-6 border-border bg-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold flex items-center gap-2">
                <ListChecks size={18} className="text-primary" /> {t("bulk.filesReady")}
              </h3>
              <span className="text-xs text-muted-foreground tabular-nums">
                {files.length} · {(totalSize / 1024 / 1024).toFixed(1)} MB
              </span>
            </div>
            <div className="divide-y divide-border max-h-96 overflow-y-auto">
              {files.map((f, idx) => (
                <div key={idx} className="py-3 flex items-center gap-3" data-testid={`bulk-file-${idx}`}>
                  <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                    <FileText size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{f.name}</p>
                    <p className="text-xs text-muted-foreground">{(f.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeFile(idx)} data-testid={`bulk-remove-${idx}`}>
                    <X size={14} />
                  </Button>
                </div>
              ))}
            </div>
          </Card>

          <Card className="lg:col-span-2 p-6 border-border bg-card space-y-4">
            <h3 className="font-display font-semibold">{t("print.settings")}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("common.copies")}</Label>
                <Input type="number" min={1} value={form.copies}
                  onChange={(e) => setForm({ ...form, copies: Math.max(1, parseInt(e.target.value) || 1) })}
                  className="mt-1.5" data-testid="bulk-copies-input" />
              </div>
              <div>
                <Label>{t("common.paper")}</Label>
                <Select value={form.paper_size} onValueChange={(v) => setForm({ ...form, paper_size: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="bulk-paper-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAPER.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg border border-border">
              <Label>{t("common.color")}</Label>
              <Switch checked={form.color} onCheckedChange={(v) => setForm({ ...form, color: v })} data-testid="bulk-color-switch" />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg border border-border">
              <Label>{t("common.duplex")}</Label>
              <Switch checked={form.duplex} onCheckedChange={(v) => setForm({ ...form, duplex: v })} data-testid="bulk-duplex-switch" />
            </div>
            <div>
              <Label>{t("orders.printer")}</Label>
              <Select value={form.printer_id} onValueChange={(v) => setForm({ ...form, printer_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="bulk-printer-select">
                  <SelectValue placeholder={printers.length ? "Select" : "No printers"} />
                </SelectTrigger>
                <SelectContent>
                  {printers.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} · {p.status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={submit}
              disabled={submitting || files.length === 0 || !form.printer_id}
              className="w-full h-11 bg-primary hover:bg-brand-blue-hover"
              data-testid="bulk-submit-btn"
            >
              {submitting ? <><Loader2 className="animate-spin mr-2" size={16} /> {t("print.sending")}</> :
               <><IndianRupee size={14} className="mr-1" /> {t("bulk.printAllQueue", { count: files.length })}</>}
            </Button>
            <p className="text-[11px] text-center text-muted-foreground">{t("wallet.currentBalance")}: ₹{Number(user.wallet_balance || 0).toFixed(2)}</p>
          </Card>
        </div>
      )}

      {results && (
        <Card className="p-6 border-border bg-card" data-testid="bulk-results">
          <h3 className="font-display font-semibold mb-4 flex items-center gap-2">
            <CheckCircle2 size={18} className="text-brand-green" /> Results
          </h3>
          <div className="divide-y divide-border">
            {results.results.map((r, i) => (
              <div key={i} className="py-2 flex items-center gap-3 text-sm">
                {r.order ? <CheckCircle2 size={14} className="text-brand-green" /> : <X size={14} className="text-destructive" />}
                <span className="flex-1 truncate">{r.filename}</span>
                <span className={`text-xs ${r.order ? "text-brand-green" : "text-destructive"}`}>
                  {r.order ? `Queued · ₹${r.order.cost}` : r.error}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
